// ===== AGB STORE CONFIG MODULE =====
// Handles multi-store configuration (US/EU/IL) with handle mapping and currency support
AGB.define('store-config', ['core'], function (Core) {
    // Store region detection
    function getStoreRegion() {
        // Check for explicit override
        if (typeof window.AGB_STORE_REGION === 'string') {
            const region = window.AGB_STORE_REGION.toLowerCase();
            if (['us', 'eu', 'il'].includes(region)) {
                return region;
            }
        }

        // Auto-detect from URL/domain
        const hostname = window.location.hostname.toLowerCase();
        if (hostname.includes('agilite.co.il')) return 'il';
        if (hostname.includes('agiliteinternational.com')) return 'eu';
        if (hostname.includes('agilitegear.com')) return 'us';

        // Fallback: Auto-detect from currency symbol
        const currency = window.AGB_CURRENCY || '$';
        if (currency === '€') return 'eu';
        if (currency === '₪') return 'il';
        return 'us'; // Default to US
    }

    // Handle mapping tables
    const HANDLE_MAPPINGS = {
        eu: {
            'k-zero-plate-carrier-copy': 'k-zero™-plate-carrier-test',
            'k19-plate-carrier-3-0-copy': 'k19-plate-carrier-3-0-test',
            'reaper-rig-micro-rig-bundle': 'reaper-rig-micro-rig-bundle',
            'warfighter-wings': 'warfighter-wings',
            'k-zero-sf-plate-carrier': 'k-zero-sf-plate-carrier',
            'k-zero-sf-bundle': 'k-zero-sf-plate-carrier-bundle'
            // Note: sub-zero-plate-carrier-copy and reaper-rig-test have no EU mapping (marked with /)
            // They will return original handle and be silently ignored if product doesn't exist
        },
        il: {
            'glove-hanger': 'מתלה-כפפות-טקטי',
            'pincer-placard': 'pincer™-5-56-פונדת-מחסניות-סקוטש-קשיחה'
        },
        us: {
            // AGB's canonical 'glove-hanger' name doesn't exist as a Shopify product
            // in the US store; the real product is at /products/agilite-free-glove-hanger.
            // Without this mapping, agb-uipack tries to fetch /products/glove-hanger.js and 404s
            // on every variant change in bundles that include the glove hanger.
            'glove-hanger': 'agilite-free-glove-hanger'
        }
    };

    // Currency mapping
    const CURRENCY_MAP = {
        us: '$',
        eu: '€',
        il: '₪'
    };

    // Handles that should skip canvas rendering (keep gallery visible)
    const CANVAS_SKIP_HANDLES = [
        'glove-hanger',
        'scorpion-patch',
        'agilite-logo-patches',
        'ir-us-flag-patch',
        'israeli-flag-patch-od-green',
        '4th-of-july-us-agilite-scorpion-patch',
        '4th-of-july-us-scorpion-patch',
        'reaper-scorpion-patch',
        'blackout-scorpion-patch',
        'black-scorpion-patch',
        'sar-orange-scorpion-patch',
        'navy-scorpion-patch',
        'purple-scorpion-patch',
        'blood-red-scorpion-patch',
        'red-scorpion-patch',
        'sar-orange-agilite-scorpion-patch',
        'santa-scorpion-patch',
        'cyber-scorpion-patch',
        't-e-testing-team-patch-private-shooter',
        't-e-testing-team-patch-military',
        't-e-testing-team-patch-law-enforcement',
        't-e-battle-belt-patch',
        't-e-sub-zero-patch',
        't-e-k-zero-patch',
        't-e-warfighter-patch',
        'testin2443223443223442432gteam-7',
        't-e-micro-map-patch',
        't-e-testing-team-3-patch',
        'testing43432432234234423324team-2',
        'testingt4242343324432eam-9',
        'hi-vis-yellow-scorpion-patch',
        'testing-team-10-hat',
        'testing-team-3-hat',
        'testingtea5355453542234432m-8',
        'testingte43343234242am-11432432432234',
        'testingteam-3',
        'testin43243344424gteam-4',
        'testin76355335454234gteam-5',
        'testin42432432gteam-1034343244',
        'tes4234322434322tingteam-1243243223',
        'testingteam-can423234a4324432432da',
        'testing-team-4-hat',
        'testing-team-12-hat',
        'testing-team-9-hat',
        'testing-team-2-hat',
        'testing-team-5-hat',
        'testing-team-7-hat',
        'testing-team-11-hat',
        'testing-team-8-hat',
        'testing-team-canada-hat',
        'testing-team-alpha-hat',
        'santa-scorpion-ornament',
        'testingte545454543434am-1',
        'forest-green-scorpion-patch',
        'glow-in-the-dark-scorpion-patch',
        't-e-testing-team-11-patch',
        't-e-testing-team-5-patch',
        't-e-testing-team-4-patch2rfwefwefqewrfgruuuj76hg',
        't-e-testing-team-patch-military',
        'testing-team-alpha-patch4526',
        'agilite-glove-hanger',
        'agilite-free-glove-hanger',
        't-e-testing-team-green-patch',
        't-e-testing-team-brown-patch',
        'testing-team-germany6587',
        'testing-team-france5428',
        'testing-team-netherlands3648',
        'testing-team-italy7628',
        'testing-team-belgium8417',
        'testing-team-uk9741',
        'rear-pouch',
        'magnetix-battle-belt',
        'magnetix-battle-belt-copy',
        'old-sizing-magnetix-battle-belt',
        'testingteam-canad5522423243a-copy'
    ];

    // Check if canvas should be skipped for a given handle
    function shouldSkipCanvas(handle) {
        if (!handle || typeof handle !== 'string') return false;
        // Reverse map to US handle for comparison
        const usHandle = reverseMapHandle(handle);
        return CANVAS_SKIP_HANDLES.includes(usHandle);
    }

    // Map a US handle to store-specific handle
    function mapHandle(handle) {
        if (!handle || typeof handle !== 'string') return handle;

        const region = getStoreRegion();
        const mappings = HANDLE_MAPPINGS[region];
        if (!mappings) return handle; // Unknown region, return original

        // Return mapped handle if exists, otherwise return original.
        // US gets checked too now so we can rewrite canonical AGB handles like
        // 'glove-hanger' to their real Shopify slugs (e.g. 'agilite-free-glove-hanger').
        return mappings[handle] || handle;
    }

    // Get currency symbol for current store
    function getCurrency() {
        const region = getStoreRegion();
        return CURRENCY_MAP[region] || '$';
    }

    // Map an array of handles
    function mapHandles(handles) {
        if (!Array.isArray(handles)) return handles;
        return handles.map(handle => mapHandle(handle));
    }

    // Map an object's keys (for objects where keys are handles)
    function mapObjectKeys(obj) {
        if (!obj || typeof obj !== 'object') return obj;
        const mapped = {};
        for (const [key, value] of Object.entries(obj)) {
            const mappedKey = mapHandle(key);
            // If value is an object/array, recursively map if it contains handles
            if (Array.isArray(value)) {
                mapped[mappedKey] = mapHandles(value);
            } else if (value && typeof value === 'object') {
                mapped[mappedKey] = mapObjectKeys(value);
            } else {
                mapped[mappedKey] = value;
            }
        }
        return mapped;
    }

    // Reverse map: get US handle from store-specific handle
    function reverseMapHandle(storeHandle) {
        if (!storeHandle || typeof storeHandle !== 'string') return storeHandle;
        const region = getStoreRegion();
        if (region === 'us') return storeHandle;
        const mappings = HANDLE_MAPPINGS[region];
        if (!mappings) return storeHandle;
        const reverseEntry = Object.entries(mappings).find(([_, mapped]) => mapped === storeHandle);
        return reverseEntry ? reverseEntry[0] : storeHandle;
    }

    return {
        getStoreRegion,
        mapHandle,
        mapHandles,
        mapObjectKeys,
        getCurrency,
        reverseMapHandle,
        shouldSkipCanvas,
        HANDLE_MAPPINGS // Expose for reverse lookups
    };
});

